import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { createScrollCoordinator, type ScrollCoordinator, type ScrollToTileOptions } from './scroll-coordinator';
import type {
    LayoutLifecycleCallback,
    LayoutLifecycleEvent,
    TileErrorBoundaryComponent,
    TileRegistration,
    TileWidthStyle,
    TilingEngineContextValue,
} from './types';

const TilingEngineContext = createContext<TilingEngineContextValue | null>(null);

const TileWidthContext = createContext<ReadonlyMap<string, TileWidthStyle> | null>(null);

type TileSlotContextValue = {
    readonly tileSlots: ReadonlyMap<string, HTMLElement>;
    readonly setTileSlot: (id: string, element: HTMLElement | null) => void;
};

const TileSlotContext = createContext<TileSlotContextValue | null>(null);

const TileErrorBoundaryContext = createContext<TileErrorBoundaryComponent | null>(null);

export const TileWidthProvider = TileWidthContext.Provider;

export const TileErrorBoundaryProvider = TileErrorBoundaryContext.Provider;

export const useTileErrorBoundary = (): TileErrorBoundaryComponent | null => useContext(TileErrorBoundaryContext);

export const TilingEngineProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [registeredTiles, setRegisteredTiles] = useState<readonly TileRegistration[]>([]);
    const [tileSlots, setTileSlots] = useState<ReadonlyMap<string, HTMLElement>>(new Map());
    const [tileOrder, setTileOrderState] = useState<readonly string[]>([]);

    const subscribersRef = useRef<Set<LayoutLifecycleCallback>>(new Set());
    const activeAnimationsRef = useRef<Set<string>>(new Set());
    const containerRef = useRef<HTMLDivElement | null>(null);
    const allTileIdsRef = useRef<string[]>([]);
    const tileBoxRefs = useRef<Map<string, HTMLElement>>(new Map());
    const endReservationsRef = useRef<Map<string, number>>(new Map());

    const setTileSlot = useCallback((id: string, element: HTMLElement | null) => {
        setTileSlots((previous) => {
            const next = new Map(previous);

            if (is.null(element)) {
                next.delete(id);
            } else {
                next.set(id, element);
            }

            return next;
        });
    }, []);

    const setTileBox = useCallback((id: string, element: HTMLElement | null) => {
        if (is.null(element)) {
            tileBoxRefs.current.delete(id);
        } else {
            tileBoxRefs.current.set(id, element);
        }
    }, []);

    const setEndReservation = useCallback((id: string, widthPx: number | null) => {
        if (widthPx === null || widthPx <= 0) {
            endReservationsRef.current.delete(id);
        } else {
            endReservationsRef.current.set(id, widthPx);
        }
    }, []);

    const setTileOrder = useCallback((order: readonly string[]) => {
        setTileOrderState((previous) => {
            if (previous.length === order.length && previous.every((id, index) => id === order[index])) {
                return previous;
            }

            return order;
        });
    }, []);

    const tileSlotContextValue = useMemo<TileSlotContextValue>(
        () => ({ tileSlots, setTileSlot }),
        [tileSlots, setTileSlot],
    );

    const notify = useCallback((event: LayoutLifecycleEvent) => {
        if (event.type === 'tileOpenStart' || event.type === 'tileCloseStart') {
            if (event.animated !== false) {
                activeAnimationsRef.current.add(event.tileId);
            }
        }

        for (const callback of subscribersRef.current) {
            callback(event);
        }

        if (event.type === 'tileOpenComplete' || event.type === 'tileCloseComplete') {
            if (event.animated !== false) {
                activeAnimationsRef.current.delete(event.tileId);

                if (activeAnimationsRef.current.size === 0) {
                    const settledEvent: LayoutLifecycleEvent = {
                        type: 'layoutSettled',
                        tileIds: allTileIdsRef.current,
                    };

                    for (const callback of subscribersRef.current) {
                        callback(settledEvent);
                    }
                }
            }
        }
    }, []);

    const subscribe = useCallback((callback: LayoutLifecycleCallback) => {
        subscribersRef.current.add(callback);

        return () => {
            subscribersRef.current.delete(callback);
        };
    }, []);

    useEffect(() => {
        return () => {
            subscribersRef.current.clear();
        };
    }, []);

    const scrollCoordinatorRef = useRef<ScrollCoordinator | null>(null);

    if (is.null(scrollCoordinatorRef.current)) {
        scrollCoordinatorRef.current = createScrollCoordinator({
            activeAnimationsRef,
            containerRef,
            endReservationsRef,
            subscribe,
            tileBoxRefs,
        });
    }

    const registerTile = useCallback((registration: TileRegistration) => {
        setRegisteredTiles((previous) => {
            const existingIndex = previous.findIndex((tile) => tile.id === registration.id);

            if (existingIndex >= 0) {
                return previous.map((tile, index) => (index === existingIndex ? registration : tile));
            }

            if (registration.pinRight === true) {
                return [...previous, registration];
            }

            const firstPinnedIndex = previous.findIndex((tile) => tile.pinRight === true);

            if (firstPinnedIndex < 0) {
                return [...previous, registration];
            }

            return [...previous.slice(0, firstPinnedIndex), registration, ...previous.slice(firstPinnedIndex)];
        });

        return () => {
            setRegisteredTiles((previous) => previous.filter((tile) => tile.id !== registration.id));
        };
    }, []);

    const unregisterTile = useCallback((id: string) => {
        setRegisteredTiles((previous) => previous.filter((tile) => tile.id !== id));
    }, []);

    const hasTile = useCallback((id: string) => registeredTiles.some((tile) => tile.id === id), [registeredTiles]);

    const contextValue = useMemo<TilingEngineContextValue>(
        () => ({
            allTileIdsRef,
            containerRef,
            hasTile,
            notify,
            registeredTiles,
            registerTile,
            scrollCoordinator: scrollCoordinatorRef.current as ScrollCoordinator,
            setEndReservation,
            setTileBox,
            setTileOrder,
            subscribe,
            tileBoxRefs,
            tileOrder,
            unregisterTile,
        }),
        [
            registerTile,
            unregisterTile,
            hasTile,
            registeredTiles,
            subscribe,
            notify,
            setEndReservation,
            setTileBox,
            setTileOrder,
            tileOrder,
        ],
    );

    return (
        <TilingEngineContext.Provider value={contextValue}>
            <TileSlotContext.Provider value={tileSlotContextValue}>{children}</TileSlotContext.Provider>
        </TilingEngineContext.Provider>
    );
};

export const useTilingEngine = (): {
    readonly registerTile: (registration: TileRegistration) => () => void;
    readonly unregisterTile: (id: string) => void;
    readonly hasTile: (id: string) => boolean;
} => {
    const context = useContext(TilingEngineContext);

    if (is.null(context)) {
        throw new Error('useTilingEngine must be used within TilingEngineProvider');
    }

    return {
        registerTile: context.registerTile,
        unregisterTile: context.unregisterTile,
        hasTile: context.hasTile,
    };
};

export const useTilingLifecycle = (options?: {
    readonly tileId?: string;
    readonly onSettled?: () => void;
}): {
    readonly subscribe: (callback: LayoutLifecycleCallback) => () => void;
} => {
    const context = useContext(TilingEngineContext);

    if (is.null(context)) {
        throw new Error('useTilingLifecycle must be used within TilingEngineProvider');
    }

    const onSettled = options?.onSettled;
    const tileId = options?.tileId;

    useEffect(() => {
        if (is.undefined(onSettled)) {
            return;
        }

        return context.subscribe((event) => {
            if (event.type !== 'layoutSettled') {
                return;
            }

            if (is.string(tileId) && !event.tileIds.includes(tileId)) {
                return;
            }

            onSettled();
        });
    }, [context, onSettled, tileId]);

    return { subscribe: context.subscribe };
};

const useTileSlotContext = (): TileSlotContextValue => {
    const context = useContext(TileSlotContext);

    if (is.null(context)) {
        throw new Error('useTileSlotContext must be used within TilingEngineProvider');
    }

    return context;
};

export const useTileSlot = (id: string): HTMLElement | null => {
    return useTileSlotContext().tileSlots.get(id) ?? null;
};

export const useSetTileSlot = (): TileSlotContextValue['setTileSlot'] => {
    return useTileSlotContext().setTileSlot;
};

export const useSetTileBox = (): TilingEngineContextValue['setTileBox'] => {
    return useTilingEngineContext().setTileBox;
};

// tileOrder is DOM-ordered: Main first, then panels and docks (incl. collapsed).
export const useTilingScroll = (): {
    readonly tileOrder: readonly string[];
    readonly scrollToTile: (id: string, options?: ScrollToTileOptions) => Promise<void>;
    readonly scrollToTileIndex: (index: number, options?: ScrollToTileOptions) => Promise<void>;
} => {
    const { scrollCoordinator, tileOrder } = useTilingEngineContext();

    const scrollToTile = useCallback(
        (id: string, options?: ScrollToTileOptions) => scrollCoordinator.scrollToTile(id, options),
        [scrollCoordinator],
    );

    const scrollToTileIndex = useCallback(
        (index: number, options?: ScrollToTileOptions): Promise<void> => {
            const length = tileOrder.length;

            if (length === 0) {
                return Promise.resolve();
            }

            const normalizedIndex = index < 0 ? length + index : index;

            if (normalizedIndex < 0 || normalizedIndex >= length) {
                return Promise.resolve();
            }

            const id = tileOrder[normalizedIndex];

            if (is.undefined(id)) {
                return Promise.resolve();
            }

            return scrollCoordinator.scrollToTile(id, options);
        },
        [scrollCoordinator, tileOrder],
    );

    return { scrollToTile, scrollToTileIndex, tileOrder };
};

/** @deprecated Use `useTilingScroll().scrollToTile(id, { align: 'end' })`. */
export const useScrollToTile = (): ((id: string, behavior?: ScrollBehavior) => void) => {
    const { scrollCoordinator } = useTilingEngineContext();

    return useCallback(
        (id, behavior = 'smooth') => {
            void scrollCoordinator.scrollToTile(id, { align: 'end', behavior });
        },
        [scrollCoordinator],
    );
};

export const useTileWidthContext = (): ReadonlyMap<string, TileWidthStyle> => {
    const context = useContext(TileWidthContext);

    if (is.null(context)) {
        throw new Error('useTileWidthContext must be used within TileWidthProvider');
    }

    return context;
};

export const useTilingEngineContext = (): TilingEngineContextValue => {
    const context = useContext(TilingEngineContext);

    if (is.null(context)) {
        throw new Error('useTilingEngineContext must be used within TilingEngineProvider');
    }

    return context;
};
